import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/admin.css';
import { getApiEndpoint } from '../config/api';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1 = OTP input, 2 = New password
    const [otp, setOtp] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [sendingOTP, setSendingOTP] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const hasSentOTP = useRef(false); // Prevent double send in React Strict Mode

    // Auto-send OTP when component mounts (only once)
    useEffect(() => {
        if (!hasSentOTP.current) {
            hasSentOTP.current = true;
            sendOTP();
        }
    }, []);

    const sendOTP = async () => {
        setSendingOTP(true);
        setError('');
        setMessage('Sending OTP to admin email...');

        try {
            const response = await axios.post(
                getApiEndpoint('auto_send_otp.php')
            );

            console.log('OTP Response:', response.data); // Debug log

            if (response.data.success) {
                setOtpSent(true);
                setMessage('✅ OTP sent to your email! Please check your inbox and enter the code below.');
            } else {
                // Show error but still allow OTP entry
                // This handles cases where email sends but API returns error
                setError(`⚠️ ${response.data.message || 'Failed to send OTP'}. If you received an OTP in your email, you can still enter it below.`);
                setOtpSent(true); // Still show OTP input field
            }
        } catch (error) {
            console.error('OTP Send Error:', error);
            // Show error but still allow manual OTP entry
            setError('⚠️ Error communicating with server. If you received an OTP in your email, you can enter it below.');
            setOtpSent(true); // Still show OTP input field
        } finally {
            setSendingOTP(false);
        }
    };

    // Step 1: Verify OTP
    const handleVerifyOTP = async (e) => {
        e.preventDefault();

        if (otp.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }

        setLoading(true);
        setError('');
        setMessage('');

        // Move to step 2 - actual verification happens on password reset
        setStep(2);
        setMessage('OTP ready! Now set your new password.');
        setLoading(false);
    };

    // Step 2: Reset Password
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');

        // Validate passwords match
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        // Validate password length
        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post(
                getApiEndpoint('verify_otp_simple.php'),
                { otp, password }
            );

            if (response.data.success) {
                setMessage('✅ Password reset successful! Redirecting to login...');
                setTimeout(() => {
                    navigate('/admin/login');
                }, 2000);
            } else {
                setError(response.data.message || 'Failed to reset password');
                // If OTP is invalid, go back to step 1
                if (response.data.message.includes('OTP')) {
                    setStep(1);
                    setOtp('');
                }
            }
        } catch (error) {
            console.error('Password reset error:', error);
            setError(error.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = () => {
        setOtp('');
        setError('');
        setMessage('');
        sendOTP();
    };

    return (
        <div className="admin-login-container">
            <div className="admin-login-card">
                <div className="admin-login-header">
                    <h2>🔐 Reset Password</h2>
                    <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem' }}>
                        {step === 1 ? 'Enter the OTP sent to your email' : 'Create your new password'}
                    </p>
                </div>

                {/* Messages */}
                {message && (
                    <div className="success-message" style={{
                        backgroundColor: '#d1fae5',
                        color: '#065f46',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        marginBottom: '1rem',
                        border: '1px solid #6ee7b7',
                        fontSize: '0.875rem'
                    }}>
                        {message}
                    </div>
                )}

                {error && (
                    <div className="error-message" style={{
                        backgroundColor: '#fee2e2',
                        color: '#991b1b',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        marginBottom: '1rem',
                        border: '1px solid #fca5a5',
                        fontSize: '0.875rem'
                    }}>
                        {error}
                    </div>
                )}

                {sendingOTP && (
                    <div style={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: '#64748b'
                    }}>
                        <div className="spinner" style={{
                            border: '3px solid #f3f4f6',
                            borderTop: '3px solid #6BAC9F',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 1rem'
                        }}></div>
                        <p>Sending OTP to admin email...</p>
                    </div>
                )}

                {/* Step 1: OTP Input */}
                {!sendingOTP && otpSent && step === 1 && (
                    <form onSubmit={handleVerifyOTP}>
                        <div className="form-group">
                            <label className="admin-label">OTP Code</label>
                            <input
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="000000"
                                required
                                maxLength="6"
                                autoFocus
                                className="admin-input"
                                style={{
                                    marginBottom: '1rem',
                                    fontSize: '1.5rem',
                                    letterSpacing: '0.5rem',
                                    textAlign: 'center',
                                    fontWeight: '600'
                                }}
                            />
                            <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '-0.5rem', marginBottom: '1rem' }}>
                                Enter the 6-digit OTP sent to your email
                            </p>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || otp.length !== 6}
                            className="admin-btn-primary"
                            style={{ width: '100%', marginBottom: '1rem' }}
                        >
                            Verify OTP & Continue
                        </button>

                        <button
                            type="button"
                            onClick={handleResendOTP}
                            className="admin-btn-secondary"
                            style={{ width: '100%', marginBottom: '1rem' }}
                        >
                            Resend OTP
                        </button>

                        <Link
                            to="/admin/login"
                            style={{
                                display: 'block',
                                textAlign: 'center',
                                color: '#6BAC9F',
                                textDecoration: 'none',
                                fontSize: '0.875rem'
                            }}
                        >
                            ← Back to Login
                        </Link>
                    </form>
                )}

                {/* Step 2: New Password */}
                {step === 2 && (
                    <form onSubmit={handleResetPassword}>
                        <div style={{
                            backgroundColor: '#f8fafc',
                            padding: '0.75rem',
                            borderRadius: '0.5rem',
                            marginBottom: '1rem',
                            fontSize: '0.875rem',
                            color: '#64748b'
                        }}>
                            OTP: <strong>{otp}</strong> (will be verified when you submit)
                        </div>

                        <div className="form-group">
                            <label className="admin-label">New Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter new password (min 6 characters)"
                                required
                                autoFocus
                                className="admin-input"
                                style={{ marginBottom: '1rem' }}
                            />
                        </div>

                        <div className="form-group">
                            <label className="admin-label">Confirm Password</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                required
                                className="admin-input"
                                style={{ marginBottom: '1.5rem' }}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="admin-btn-primary"
                            style={{ width: '100%' }}
                        >
                            {loading ? 'Resetting Password...' : 'Reset Password'}
                        </button>
                    </form>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default ForgotPassword;
